import { fetchUtils } from 'react-admin';

interface ChatbotHookProps {
  apiUrl: string;
  fetchJson: (
    url: string,
    options?: fetchUtils.Options,
  ) => Promise<{ json: any }>;
}

export const chatbotService = ({ apiUrl, fetchJson }: ChatbotHookProps) => {
  // Special case handler for chatbot creation with settings
  const handleChatbotCreate = async (params: any) => {
    const { data } = params;
    const { settings, ...chatbotData } = data;

    try {
      // First, create the chatbot settings
      const settingsResponse = await fetchJson(
        `${apiUrl}/api/v1/chatbots_settings`,
        {
          method: 'POST',
          body: JSON.stringify({ data: settings || {} }),
        },
      );

      // Access the data property directly, as fetchJson already parses the JSON
      const settingsResult = settingsResponse.json.data;

      if (!settingsResult || !settingsResult.id) {
        throw new Error(
          'Failed to create chatbot settings: No valid ID returned',
        );
      }

      console.log('Created settings with ID:', settingsResult.id);

      // Now create the chatbot with the setting ID
      const enhancedChatbotData = {
        ...chatbotData,
        chatbot_setting_id: settingsResult.id,
      };

      const chatbotResponse = await fetchJson(`${apiUrl}/api/v1/chatbots`, {
        method: 'POST',
        body: JSON.stringify({ data: enhancedChatbotData }),
      });

      // Access the data property directly, as fetchJson already parses the JSON
      const chatbot = chatbotResponse.json.data;

      // Return the created chatbot with its settings
      return {
        data: {
          ...chatbot,
          settings: settingsResult,
        },
      };
    } catch (error) {
      console.error('Error creating chatbot:', error);
      throw error;
    }
  };

  // Special case handler for chatbot updates with settings
  const handleChatbotUpdate = async (params: any) => {
    const { id, data } = params;
    const { settings, ...chatbotData } = data;

    try {
      // First, update the chatbot settings if provided
      if (settings) {
        // Get the chatbot to find its settings ID
        const chatbotResponse = await fetchJson(
          `${apiUrl}/api/v1/chatbots/${id}`,
        );
        const chatbot = chatbotResponse.json.data;

        if (chatbot.chatbot_setting_id) {
          // Update the settings
          await fetchJson(
            `${apiUrl}/api/v1/chatbots_settings/${chatbot.chatbot_setting_id}`,
            {
              method: 'PUT',
              body: JSON.stringify({ data: settings }),
            },
          );
        }
      }

      // Now update the chatbot
      const response = await fetchJson(`${apiUrl}/api/v1/chatbots/${id}`, {
        method: 'PUT',
        body: JSON.stringify({ data: chatbotData }),
      });

      const updatedChatbot = response.json.data;

      // Get the updated settings to return
      const settingsResponse = await fetchJson(
        `${apiUrl}/api/v1/chatbots_settings/${updatedChatbot.chatbot_setting_id}`,
      );
      const updatedSettings = settingsResponse.json.data;

      // Return the updated chatbot with its settings
      return {
        data: {
          ...updatedChatbot,
          settings: updatedSettings,
        },
      };
    } catch (error) {
      console.error('Error updating chatbot:', error);
      throw error;
    }
  };

  // Modify the getOne method to include settings
  const handleChatbotGetOne = async (params: any) => {
    try {
      // Explicitly use 'chatbots' resource name
      const response = await fetchJson(
        `${apiUrl}/api/v1/chatbots/${params.id}`,
      );
      const chatbot = response.json.data;

      if (!chatbot) {
        console.error(`No chatbot found with id ${params.id}`);
        return { data: {} }; // Return empty data to prevent errors
      }

      // Get the settings if there's a chatbot_setting_id and it's valid
      if (
        chatbot.chatbot_setting_id &&
        chatbot.chatbot_setting_id !== 'undefined'
      ) {
        // This fetch is correct as it explicitly uses 'chatbots_settings'
        const settingsResponse = await fetchJson(
          `${apiUrl}/api/v1/chatbots_settings/${chatbot.chatbot_setting_id}`,
        );
        const settings = settingsResponse.json.data;

        // Return chatbot with settings ONLY if settings are successfully fetched
        if (settings) {
          return {
            data: {
              ...chatbot,
              settings,
            },
          };
        } else {
          console.warn(
            `Chatbot ${params.id} has setting ID ${chatbot.chatbot_setting_id}, but settings could not be fetched.`,
          );
          // Fallback: return just the chatbot data if settings fetch fails
          return { data: chatbot };
        }
      }

      // If no valid setting ID, return just the chatbot data
      return { data: chatbot };
    } catch (error) {
      console.error('Error fetching chatbot or its settings:', error);
      // Ensure we always return the expected structure even on error
      if ((error as any).status === 404) {
        console.warn(
          `Chatbot or associated setting not found for id ${params.id}.`,
        );
        // You might want to return an empty object or a specific structure indicating not found
        return { data: { id: params.id } }; // Return at least the ID if possible
      }
      return { data: {} }; // General error case
    }
  };

  // Update the getOne method for chatbot_settings to include chatbots
  const handleChatbotSettingsGetOne = async (params: any) => {
    try {
      // Make sure to explicitly use 'chatbots_settings' as the resource
      const response = await fetchJson(
        `${apiUrl}/api/v1/chatbots_settings/${params.id}`,
      );

      // Check if we have a valid response with data
      if (!response.json || !response.json.data) {
        console.error(
          `No valid data in response for chatbot settings ${params.id}`,
        );
        return { data: {} };
      }

      const chatbotSetting = response.json.data;

      // Get associated chatbots
      const chatbotsResponse = await fetchJson(
        `${apiUrl}/api/v1/chatbots?chatbot_setting_id=${params.id}`,
        { method: 'GET' },
      );

      const chatbots =
        chatbotsResponse.json && chatbotsResponse.json.data
          ? chatbotsResponse.json.data
          : [];

      // Add these to the chatbot_setting object
      return {
        data: {
          ...chatbotSetting,
          chatbots: chatbots.map((c: any) => c.id),
        },
      };
    } catch (error) {
      console.error('Error fetching chatbot settings:', error);
      // Return empty data rather than throwing to avoid UI breakage
      return { data: {} };
    }
  };

  // Special case handler for chatbot settings update
  const handleChatbotSettingsUpdate = async (params: any) => {
    const { id, data } = params;
    const { chatbots, ...chatbotSettingData } = data;

    // Update the chatbot setting first - wrap in data object
    const response = await fetchJson(
      `${apiUrl}/api/v1/chatbots_settings/${id}`,
      {
        method: 'PUT',
        body: JSON.stringify({ data: chatbotSettingData }),
      },
    );

    const chatbotSetting = await response.json;

    // If we have chatbots defined, update them to point to this setting
    if (chatbots && chatbots.length > 0) {
      await Promise.all(
        chatbots.map((chatbotId: string) =>
          fetchJson(`${apiUrl}/api/v1/chatbots/${chatbotId}`, {
            method: 'PATCH',
            body: JSON.stringify({ data: { chatbot_setting_id: id } }),
          }),
        ),
      );
    }

    return { data: { ...chatbotSetting, chatbots } };
  };

  return {
    handleChatbotCreate,
    handleChatbotUpdate,
    handleChatbotGetOne,
    handleChatbotSettingsGetOne,
    handleChatbotSettingsUpdate,
  };
};
